import { FiAlignLeft } from 'react-icons/fi';

export const basicParagraphStructureData = {
  courseId: 'basic-paragraph-structure',
  courseType: 'writing-course',
  title: 'Basic Paragraph Structure',
  icon: FiAlignLeft,
  overview: {
    impact: 'Build clear, well-organized paragraphs',
    focus: 'Topic sentences, supporting details, concluding sentences, paragraph unity',
    uses: 'Writing Tasks 1 & 2',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'What Is a Paragraph?',
      content: `<p>A paragraph is a group of related sentences that develop <strong>one single idea</strong>. In IELTS writing, each body paragraph should focus on one main point and be clearly separated from the next.</p>
      <p>A well-structured paragraph has three parts:</p>`,
      points: [
        '<strong>Topic sentence</strong> — states the main idea of the paragraph (usually the first sentence)',
        '<strong>Supporting sentences</strong> — explain, illustrate, or provide evidence for the main idea',
        '<strong>Concluding sentence</strong> — summarises the point or links to the next paragraph',
      ],
      examples: [
        {
          label: 'Well-structured paragraph',
          text: 'Public transport is the most effective way to reduce urban traffic congestion. (Topic sentence) Buses and trains can carry hundreds of passengers at once, replacing dozens of individual cars on the road. Cities like Tokyo and London have demonstrated that efficient rail networks significantly reduce rush-hour gridlock. (Supporting sentences) Therefore, investing in public transport infrastructure should be a priority for any city facing traffic problems. (Concluding sentence)',
        },
      ],
    },
    {
      title: 'Writing Strong Topic Sentences',
      content: `<p>The topic sentence is the most important sentence in your paragraph. It tells the reader <em>what the paragraph is about</em> and <em>what your position is</em>.</p>
      <p>A good topic sentence has two parts:</p>`,
      points: [
        '<strong>Topic</strong> — the subject you are writing about',
        '<strong>Controlling idea</strong> — what you will say about the topic',
        'Keep it specific enough to develop in one paragraph, but broad enough to need supporting details',
        'Avoid topic sentences that are too vague ("There are many things about education") or too narrow ("52% of students use laptops")',
      ],
      examples: [
        {
          label: 'Good topic sentence',
          text: 'One major advantage of online learning is the flexibility it offers to working professionals.',
        },
        {
          label: 'Poor topic sentence (too vague)',
          text: 'Online learning is interesting.',
        },
      ],
      warning: 'Never start a body paragraph without a clear topic sentence. IELTS examiners look for this when assessing Coherence and Cohesion (25% of your score).',
    },
    {
      title: 'Supporting and Concluding Sentences',
      content: `<p>Supporting sentences form the body of your paragraph. They develop the main idea through explanations, examples, statistics, or reasoning.</p>
      <p>The concluding sentence wraps up the paragraph. It may restate the main idea in different words, draw a conclusion, or transition to the next paragraph.</p>`,
      points: [
        'Use 2-4 supporting sentences per paragraph in IELTS essays',
        'Each supporting sentence should directly relate to the topic sentence',
        'Include specific examples — "For instance," "For example," "such as"',
        'The concluding sentence should not introduce a brand-new idea',
      ],
      table: {
        headers: ['Support Type', 'Example Phrase', 'Use In'],
        rows: [
          ['Explanation', '"This means that…" / "In other words…"', 'All essay types'],
          ['Example', '"For instance…" / "A clear example is…"', 'Task 2 especially'],
          ['Data reference', '"The figure shows…" / "According to the chart…"', 'Task 1 especially'],
          ['Result', '"As a result…" / "Consequently…"', 'Problem/Solution essays'],
        ],
      },
    },
    {
      title: 'Paragraph Unity and the One-Idea Rule',
      content: `<p>Paragraph unity means every sentence in a paragraph supports the <strong>same main idea</strong>. If a sentence does not relate to the topic sentence, it breaks unity and should be moved or removed.</p>
      <p>Common unity problems:</p>`,
      points: [
        '<strong>Off-topic sentences</strong> — information that belongs in a different paragraph',
        '<strong>Multiple main ideas</strong> — trying to cover two arguments in one paragraph',
        '<strong>Missing topic sentence</strong> — the reader cannot identify the paragraph\'s purpose',
        '<strong>Contradicting the topic sentence</strong> — supporting details that argue against your own point',
      ],
      examples: [
        {
          label: 'Paragraph with broken unity',
          text: 'Exercise improves mental health by reducing stress hormones. Regular physical activity releases endorphins that create positive feelings. The government should build more gyms. Studies show that even 30 minutes of walking can improve mood. (The sentence about government policy breaks unity — it introduces a new idea.)',
        },
        {
          label: 'Fixed version',
          text: 'Exercise improves mental health by reducing stress hormones. Regular physical activity releases endorphins that create positive feelings. Studies show that even 30 minutes of walking can improve mood. Clearly, incorporating exercise into daily routines is beneficial for psychological well-being.',
        },
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify the Topic Sentence',
      type: 'select',
      instructions: 'Choose the best topic sentence for each paragraph scenario.',
      questions: [
        {
          text: 'A paragraph about the benefits of reading for children:',
          options: [
            'Reading regularly from a young age significantly improves children\'s vocabulary and comprehension skills.',
            'Books are made of paper.',
            'Some children like reading and some do not.',
            'My favourite book as a child was Harry Potter.',
          ],
          answer: 'Reading regularly from a young age significantly improves children\'s vocabulary and comprehension skills.',
          explanation: 'This sentence has a clear topic (reading for children) and a controlling idea (improves vocabulary and comprehension). The others are too vague, too narrow, or irrelevant.',
        },
        {
          text: 'A paragraph about problems caused by fast food:',
          options: [
            'Fast food is available everywhere.',
            'The widespread consumption of fast food has contributed to rising obesity rates in many countries.',
            'I eat fast food sometimes.',
            'There are many types of fast food.',
          ],
          answer: 'The widespread consumption of fast food has contributed to rising obesity rates in many countries.',
          explanation: 'This is specific about the problem (obesity) and makes a clear claim that can be supported with evidence.',
        },
        {
          text: 'A paragraph arguing that public libraries should receive more funding:',
          options: [
            'Increasing funding for public libraries would provide communities with greater access to education and digital resources.',
            'Libraries have books.',
            'People should read more.',
            'The government spends money on many things.',
          ],
          answer: 'Increasing funding for public libraries would provide communities with greater access to education and digital resources.',
          explanation: 'It states the argument (more funding) and the controlling idea (greater access to education and digital resources).',
        },
        {
          text: 'A paragraph about the advantages of cycling to work:',
          options: [
            'Cycling to work offers significant health and environmental benefits compared to driving.',
            'Bicycles have two wheels.',
            'Many people drive to work every day.',
            'Exercise is important for everyone.',
          ],
          answer: 'Cycling to work offers significant health and environmental benefits compared to driving.',
          explanation: 'It clearly identifies the topic (cycling to work) and what will be discussed (health and environmental benefits).',
        },
      ],
    },
    {
      title: 'Order Sentences into a Paragraph',
      type: 'select',
      instructions: 'Identify the correct role of each sentence in a paragraph about tourism.',
      questions: [
        {
          text: '"Tourism brings significant economic benefits to developing countries." — This sentence is the:',
          options: ['Topic sentence', 'Supporting sentence', 'Concluding sentence'],
          answer: 'Topic sentence',
          explanation: 'It introduces the main idea (economic benefits of tourism) that the rest of the paragraph will develop.',
        },
        {
          text: '"For example, in Thailand, the tourism industry accounts for nearly 20% of GDP and employs millions of people." — This sentence is the:',
          options: ['Supporting sentence', 'Topic sentence', 'Concluding sentence'],
          answer: 'Supporting sentence',
          explanation: 'It provides a specific example to support the main idea about economic benefits.',
        },
        {
          text: '"Hotels, restaurants, and transport services all benefit from the spending of international visitors." — This sentence is the:',
          options: ['Supporting sentence', 'Topic sentence', 'Concluding sentence'],
          answer: 'Supporting sentence',
          explanation: 'It adds further explanation of how the economic benefits operate.',
        },
        {
          text: '"It is clear, therefore, that tourism plays a vital role in the economies of many developing nations." — This sentence is the:',
          options: ['Concluding sentence', 'Topic sentence', 'Supporting sentence'],
          answer: 'Concluding sentence',
          explanation: 'It summarises the paragraph\'s argument using "It is clear, therefore," signalling a conclusion.',
        },
      ],
    },
    {
      title: 'Identify Paragraph Problems',
      type: 'select',
      instructions: 'What is the main problem with each paragraph?',
      questions: [
        {
          text: '"Cities should invest in renewable energy. Solar panels are becoming cheaper. Wind turbines generate electricity. My uncle works at a power plant. Renewable energy reduces carbon emissions."',
          options: [
            'Contains an off-topic sentence that breaks paragraph unity',
            'Missing a topic sentence',
            'Too many supporting sentences',
            'No problem — this paragraph is fine',
          ],
          answer: 'Contains an off-topic sentence that breaks paragraph unity',
          explanation: '"My uncle works at a power plant" is a personal detail unrelated to the argument about renewable energy investment.',
        },
        {
          text: '"Furthermore, many students benefit from using computers. Laptops allow research. Also, social media can be distracting. Online resources are helpful."',
          options: [
            'Contains two conflicting ideas (benefits and problems) in one paragraph',
            'The paragraph is too short',
            'There are no examples',
            'The vocabulary is too simple',
          ],
          answer: 'Contains two conflicting ideas (benefits and problems) in one paragraph',
          explanation: 'The sentence about social media being distracting contradicts the paragraph\'s focus on benefits. It should be in a separate paragraph.',
        },
        {
          text: '"Walking is healthy. It reduces the risk of heart disease by up to 30%. Regular walkers report lower stress levels and better sleep quality. A 2019 study found that people who walk 10,000 steps daily live longer on average."',
          options: [
            'Missing a concluding sentence',
            'Missing a topic sentence',
            'Contains off-topic information',
            'Too many statistics',
          ],
          answer: 'Missing a concluding sentence',
          explanation: 'The paragraph has a topic sentence and strong supporting evidence but ends abruptly without a concluding sentence that ties it together.',
        },
      ],
    },
    {
      title: 'Write Topic Sentences',
      type: 'writing',
      instructions: 'Write a clear topic sentence for each paragraph scenario. Include both a topic and a controlling idea.',
      questions: [
        {
          text: 'A body paragraph about the disadvantages of social media for teenagers',
          example: 'Excessive social media use can have a detrimental effect on teenagers\' mental health and self-esteem.',
          tip: 'Name the specific disadvantage in your topic sentence — don\'t just say "social media is bad."',
        },
        {
          text: 'A body paragraph about why governments should invest in space exploration',
          example: 'Government investment in space exploration drives technological innovation that benefits everyday life on Earth.',
          tip: 'State the reason clearly — this will be the point your supporting sentences develop.',
        },
        {
          text: 'A body paragraph describing an upward trend in a Task 1 graph',
          example: 'The most striking feature of the data is the sharp increase in online shopping between 2010 and 2020.',
          tip: 'For Task 1, your topic sentence should identify what trend you are about to describe.',
        },
        {
          text: 'A conclusion paragraph for an essay about the benefits of multilingualism',
          example: 'In conclusion, learning multiple languages offers cognitive, professional, and cultural advantages that make it a worthwhile pursuit for people of all ages.',
          tip: 'Restate your thesis in different words — do not copy your introduction sentence.',
        },
      ],
    },
  ],
  tips: [
    'Always start each body paragraph with a clear topic sentence',
    'Check every sentence: does it support the main idea? If not, remove or move it',
    'Aim for 3-5 sentences per body paragraph in IELTS essays',
    'Use indentation or a blank line to separate paragraphs clearly',
  ],
  dailyPractice: [
    'Read an article and identify the topic sentence in each paragraph',
    'Write three topic sentences on different IELTS topics',
    'Take a sample essay and check each paragraph for unity — find any off-topic sentences',
    'Practice writing one complete PEEL paragraph in 5 minutes',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Basic Paragraph Structure: Building clear, well-organized paragraphs with topic sentences, supporting details, concluding sentences, and paragraph unity for IELTS Writing Tasks 1 and 2.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...basicParagraphStructureData,
      title: basicParagraphStructureData.title + ' (Foundations)',
      overview: { ...basicParagraphStructureData.overview, duration: '15-20 minutes' },
      sections: basicParagraphStructureData.sections.slice(0, Math.max(2, Math.ceil(basicParagraphStructureData.sections.length / 2))),
      exercises: basicParagraphStructureData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...basicParagraphStructureData,
      title: basicParagraphStructureData.title + ' (Advanced)',
      overview: { ...basicParagraphStructureData.overview, duration: '30-35 minutes' },
      sections: basicParagraphStructureData.sections,
      exercises: basicParagraphStructureData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(basicParagraphStructureData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return basicParagraphStructureData;
}
